import { SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import cloneDeep from "lodash-es/cloneDeep";
import isArray from "lodash-es/isArray";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import MultiSelect, {
  MultiSelectFilterChangeEvent,
  toSelectDataArray,
} from "@/components/MultiSelect";
import { useDebounce } from "@/hooks/useDebounce";
import useInfinitePageItems from "@/hooks/useInfinitePageItems";
import usePaginateQuery, { QueryMethod } from "@/hooks/usePaginateQuery";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import ListNoData from "./ListNoData";
import { FormSelectItemRender, SelectData } from "./index";

export interface IFormDropDownListPaginateProps<
  Args extends Record<string, unknown>,
  Response extends { items: DataItem[]; total: number },
  DataItem extends Record<string, unknown>,
> extends Partial<FieldRenderProps> {
  method: QueryMethod<Args, Response>;
  params?: Args;
  sort: SortDescriptor;
  perPage?: number;
  isMultiValue?: boolean;
  preservedValueProps?: Record<string, unknown>;
  parseResponse?: (items: DataItem[]) => DataItem[];
}

function FormDropDownListPaginate<
  Args extends Record<string, unknown>,
  Response extends { items: DataItem[]; total: number },
  DataItem extends Record<string, unknown>,
>(fieldRenderProps: IFormDropDownListPaginateProps<Args, Response, DataItem>) {
  const {
    validationMessage,
    touched,
    id = fieldRenderProps.name,
    label,
    valid,
    disabled,
    hint,
    noDataMessage,
    fillMode,
    floatingLabel,
    floatLabel,
    placeholder,
    FieldWrapperClassName,
    filterable = true,
    popupWidth,
    itemRender,
    value: formValue,
    hintIcon,
    animate = true,
    textField = "name",
    dataItemKey = "id",
    method,
    params = {} as Args,
    perPage = 20,
    sort,
    showHint,
    onChange,
    isMultiValue,
    preservedValueProps = {},
    parseResponse,
    valueRender: _valueRender,
    descriptionField,
    allowCustom = true,
    optional = false,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = touched && validationMessage;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = `${id}_label`;

  const emptyItem: Record<string, string> = useMemo(
    () => ({ [textField]: "Loading ..." }),
    [textField],
  );

  const [pageNo, setPageNo] = useState(1);
  const [virtualSkip, setVirtualSkip] = useState(0);
  const [pageItems, setPageItems] = useState<DataItem[]>([]);
  // How many items `parseResponse` has dropped across every page loaded so far,
  // used to correct the server `total`. Tracked per page in a ref rather than
  // accumulated with `setState(prev => prev + n)`: the effect below can re-run
  // for the same page (an unstable `parseResponse` or `items` identity), and
  // `map.set(pageNo, dropped)` is idempotent where `+=` would double-count.
  const droppedByPage = React.useRef(new Map<number, number>());
  const [filteredOut, setFilteredOut] = useState(0);
  const [value, setValue] = useState<DataItem | DataItem[] | null>(
    // Seed from formValue so chips render immediately in edit mode rather than
    // waiting for the post-mount effect cycle.
    isMultiValue ? toSelectDataArray<DataItem>(formValue) : null,
  );
  const [filter, setFilter] = useState("");
  const [isShouldCallAPI, setIsShouldCallAPI] = useState(false);

  const search = useDebounce(filter, 600);

  React.useEffect(() => {
    setValue(isMultiValue ? toSelectDataArray<DataItem>(formValue) : formValue);
  }, [formValue, isMultiValue]);

  const clearValue = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setValue(null);
      onChange?.({
        value: null,
      });
    },
    [onChange],
  );

  const valueRender = (
    element: React.ReactElement<HTMLSpanElement>,
    selectedValue: DataItem | null,
  ) => {
    if (!selectedValue) {
      return element;
    }
    const children = [
      <span
        key={1}
        style={{ overflow: "hidden", textOverflow: "ellipsis", flexGrow: 1 }}
      >
        {element.props.children as React.ReactNode}
      </span>,
      <Button
        key={2}
        fillMode={"flat"}
        iconClass="k-icon k-font-icon k-clear-value k-i-close"
        role="button"
        tabIndex={-1}
        size={"small"}
        title="Clear"
        onClick={clearValue}
      ></Button>,
    ];

    return React.cloneElement(element, { ...element.props }, children);
  };

  const {
    data: items,
    isLoading,
    isFetching,
  } = usePaginateQuery({
    method,
    params: { ...params, search } as Args,
    sort,
    perPage,
    page: pageNo,
    options: {
      enabled: isShouldCallAPI,
      retryOnMount: false,
    },
  });

  // Reset to page 1 whenever debounced search changes
  React.useEffect(() => {
    setPageNo(1);
    setVirtualSkip(0);
  }, [search]);

  // Feed only completed-fetch items into the accumulator (skip during in-flight fetches
  // to avoid useInfinitePageItems seeing stale keepPreviousData from prior pages)
  React.useEffect(() => {
    if (isLoading || isFetching) return;
    const raw = items?.items ? cloneDeep(items.items) : [];
    const next = parseResponse ? parseResponse(raw) : raw;
    setPageItems(next);
    // A shrinking `parseResponse` must not keep the raw server `total`, or
    // virtualization reserves blank rows for items that were filtered out.
    // Discarding `total` outright is not the answer either — `useInfinitePageItems`
    // then falls back to `pageItems.length >= pageSize` on the *filtered* array,
    // so a page of 20 that shrinks to 5 would stop pagination at page 1.
    // Subtracting the cumulative drop keeps both: pages keep loading, and only
    // the not-yet-loaded tail can still over-reserve.
    if (pageNo === 1) droppedByPage.current.clear();
    droppedByPage.current.set(pageNo, raw.length - next.length);
    setFilteredOut(
      [...droppedByPage.current.values()].reduce((sum, n) => sum + n, 0),
    );
  }, [items, isLoading, isFetching, parseResponse, pageNo]);

  const getItemId = useCallback(
    (item: DataItem) => item[dataItemKey as keyof DataItem] as string | number,
    [dataItemKey],
  );

  const correctedTotal =
    items?.total != null ? Math.max(items.total - filteredOut, 0) : undefined;

  const { items: allItems, hasMore } = useInfinitePageItems({
    pageItems,
    pageNo,
    pageSize: perPage,
    total: correctedTotal,
    getItemId,
  });

  const onLoadMore = useCallback(() => setPageNo(p => p + 1), []);

  // Shared handler for both MultiSelect and DropDownList — triggers initial API load on first open/click
  const handleOpenOrClick = useCallback(() => {
    if (isShouldCallAPI) return;
    setIsShouldCallAPI(true);
  }, [isShouldCallAPI]);

  // Shared by two dropdowns with different event shapes: our MultiSelect invokes
  // it with `{ filter: { value } }`, Kendo's DropDownList with its own event
  // (whose `filter.value` is optional). Only `filter.value` is ever read, so the
  // union types it for both without widening either call site to `any`.
  const onFilterChange = useCallback(
    (event: MultiSelectFilterChangeEvent | DropDownListFilterChangeEvent) => {
      const filter: string = event.filter.value ?? "";
      // only set filter if it is empty or filter length is greater than 2
      if (!filter || filter.length > 2) {
        setFilter(filter);
      }
    },
    [],
  );

  useDeepCompareEffect(() => {
    if (isLoading || isArray(value) || !value || !allItems?.length) return;

    const keyField = dataItemKey as keyof DataItem;
    const textKey = textField as keyof DataItem;
    const currentKey = value[keyField];

    if (currentKey) {
      const foundValue = allItems.find(item => item[keyField] === currentKey);
      if (foundValue && foundValue[textKey] !== value[textKey]) {
        onChange?.({
          value: { ...foundValue, ...preservedValueProps },
        });
      }
    }
  }, [
    value,
    allItems,
    dataItemKey,
    textField,
    isLoading,
    preservedValueProps,
    onChange,
  ]);

  const onInputChange = useCallback(
    (event: DropDownListChangeEvent) => {
      if (isArray(event.value)) {
        setValue(event.value);
        onChange?.({
          value: event.value,
        });
        return;
      }
      const newValue = event.target.value;
      if (newValue && newValue[textField] === emptyItem[textField]) {
        return;
      }
      setValue(newValue);
      onChange?.({
        value: event.value,
      });
    },
    [emptyItem, onChange, textField],
  );

  // For MultiSelect, empty array [] is truthy — pass falsy so FloatingLabel doesn't stay floated when empty.
  // A leftover non-array (Equals object after switching to In) is also empty in the widget.
  const labelValue =
    isMultiValue && (!Array.isArray(formValue) || formValue.length === 0)
      ? undefined
      : formValue;

  return (
    <FieldWrapper className={FieldWrapperClassName}>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={!!valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        label={label}
        value={labelValue}
        floatLabel={floatLabel}
        placeholder={placeholder}
        optional={optional}
      >
        {isMultiValue ? (
          <MultiSelect
            ariaDescribedBy={`${hindId} ${errorId}`}
            aria-invalid={valid === false || undefined}
            ariaLabelledBy={labelId}
            textField={textField}
            dataItemKey={dataItemKey}
            valid={valid}
            disabled={disabled}
            style={{ width: "100%" }}
            data={allItems as unknown as SelectData[]}
            value={value as unknown as SelectData[]}
            onFilterChange={onFilterChange}
            onChange={onInputChange as FieldRenderProps["onChange"]}
            loading={isLoading || (isFetching && pageNo === 1)}
            valueRender={
              _valueRender as unknown as (
                element: React.ReactElement<HTMLSpanElement>,
                selectedValue: SelectData,
              ) => React.ReactNode
            }
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isFetching && pageNo > 1}
            setValue={false}
            allowCustom={allowCustom}
            {...others}
            onOpen={handleOpenOrClick}
            onClick={handleOpenOrClick}
          />
        ) : (
          <DropDownList
            ariaDescribedBy={`${hindId} ${errorId}`}
            aria-invalid={valid === false || undefined}
            ariaLabelledBy={labelId}
            textField={textField}
            dataItemKey={dataItemKey}
            valid={valid}
            disabled={disabled}
            filterable={filterable}
            style={{ width: "100%" }}
            fillMode={fillMode}
            data={allItems as unknown as DataItem[]}
            value={value as DataItem | null}
            onFilterChange={onFilterChange}
            onChange={onInputChange as FieldRenderProps["onChange"]}
            loading={isLoading || isFetching}
            valueRender={
              valueRender as unknown as (
                element: React.ReactElement<HTMLSpanElement>,
                selectedValue: SelectData,
              ) => React.ReactNode
            }
            itemRender={
              itemRender
                ? itemRender
                : (li, itemProps) =>
                    FormSelectItemRender(li, {
                      ...itemProps,
                      descriptionField,
                    })
            }
            popupSettings={
              popupWidth ? { width: popupWidth, animate } : { animate }
            }
            listNoDataRender={() => (
              <ListNoData
                message={noDataMessage}
                loading={isLoading || isFetching}
              />
            )}
            virtual={{
              pageSize: perPage,
              skip: virtualSkip,
              total: correctedTotal ?? allItems.length,
            }}
            onPageChange={(e: { page: { skip: number; take: number } }) => {
              setVirtualSkip(e.page.skip);
              const requestedEnd = e.page.skip + e.page.take;
              if (
                requestedEnd > allItems.length &&
                hasMore &&
                !isFetching &&
                !isLoading
              ) {
                setPageNo(p => p + 1);
              }
            }}
            {...others}
            onOpen={handleOpenOrClick}
          />
        )}
        {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} />}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
}

export default FormDropDownListPaginate;
